import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Icons } from '../../theme/Images'
import LabeledField from './LabeledField'

export default function LabeledInput({ 
  label, 
  value, 
  onChange, 
  className = '', 
  inputClassName = '', 
  placeholder = '', 
  type = 'text',
  onValidate,
  triggerValidation,
  disabled,
  ...props 
}) {
  const [isValid, setIsValid] = useState(true)
  const [hasBlurred, setHasBlurred] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const validateField = () => {
    setHasBlurred(true)
    
    // Call custom validation if provided
    if (onValidate) {
      const validationResult = onValidate(value)
      
      // Handle both simple boolean returns and object returns
      if (typeof validationResult === 'object' && validationResult !== null) {
        // Object format: { isValid: boolean, errorMessage: string }
        if (!validationResult.isValid) {
          setIsValid(false)
          setErrorMessage(validationResult.errorMessage || 'Invalid value')
          return
        }
      } else if (!validationResult) {
        // Boolean format: just return false
        setIsValid(false)
        setErrorMessage('Invalid value')
        return
      }
    }

    // Default validation: check if field is empty
    const isEmpty = value.trim() === ''
    if (isEmpty) {
      setIsValid(false)
      setErrorMessage('This field is required')
    } else {
      setIsValid(true)
      setErrorMessage('')
    }
  }

  const handleBlur = () => {
    validateField()
  }

  // Listen for external validation trigger
  useEffect(() => {
    if (triggerValidation) {
      validateField()
    }
  }, [triggerValidation])

  const getInputClasses = () => {
    const baseClasses = "w-full px-3 pt-3 pb-2 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-[16px] leading-[24px] border font-[300]"
    
    if (disabled) {
      return `${baseClasses} border-[color:var(--Grays-Gray-5,#ECECEC)] cursor-not-allowed bg-gray-50 placeholder:text-[color:var(--Grays-Gray,#5D5D5D)] ${inputClassName}`
    }
    
    if (hasBlurred && !isValid) {
      return `${baseClasses} border-error-300 focus:ring-error-300 focus:border-error-300 bg-white placeholder:text-[color:var(--Grays-Gray,#5D5D5D)] ${inputClassName}`
    }
    
    return `${baseClasses} border-[color:var(--Grays-Gray-5,#ECECEC)] bg-white placeholder:text-[color:var(--Grays-Gray,#5D5D5D)] ${inputClassName}`
  }

  return (
    <LabeledField label={label} className={className} validationError={errorMessage}>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={getInputClasses()}
          disabled={disabled}
          style={{ color: 'var(--Grays-Gray, #5D5D5D)' }}
          {...props}
        />
        {hasBlurred && !isValid && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icons.FailureAlertIconSimple />
          </div>
        )}
      </div>
    </LabeledField>
  )
}

LabeledInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  onValidate: PropTypes.func,
  triggerValidation: PropTypes.bool,
  disabled: PropTypes.bool,
} 

